import { useEffect } from 'react'
import { useNavigate } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import { queryClient } from "index";

import {useAuthStore} from 'context/authContext'

import { TeamUser, CustomError, UserProfile, errorCodeEnum } from "core";
import { remoteHattrickInfoInteractor, localUserProfileInteractor, localAuthInteractor } from "di";

import { mapUserProfileToUncompletedSections } from "utils";

import type { ButtonSectionType } from "components/FinishSection/FinishSection";

type Data = {
  teamUsers: TeamUser[];
  uncompletedSections: ButtonSectionType[];
  completed: boolean;
};

export interface ViewInterface {
  data: Data | undefined;
  title: string;
  subtitle: string;
  layoutId: string;
  titleLayoutId?: string;
  progressBarLayoutId?: string;
  isLoading: boolean;
  isError: boolean;
  error: CustomError | null;
  color: "primary" | "secondary" | "accent1" | "accent2";
  onBackHome: () => void;
  onFinish: () => void;
}

const fetchData = async () => {
  try {
    const teamUsers =
      (await remoteHattrickInfoInteractor.getTeamUsers()) as TeamUser[];
    const userProfile = localUserProfileInteractor.getUserProfile() as UserProfile;
    return {
      teamUsers,
      uncompletedSections: mapUserProfileToUncompletedSections(userProfile),
      completed: userProfile.meetTheTeamCompleted
    };
  } catch (error) {
    throw error as CustomError;
  }
};

export default function MeetTheTeamViewModel(): ViewInterface {
  const {setIsLoggedIn} = useAuthStore();
  const { data, isLoading, isError, error } = useQuery<Data, CustomError>(
    ["teamUsers"],
    fetchData
  );

  useEffect(() => {
    if (error?.code === errorCodeEnum.SERVER_ERROR) {
      localAuthInteractor.deleteToken();
      localUserProfileInteractor.deleteUserProfile();
      alert("Es necesario voler a autenticarse");
      setIsLoggedIn(false);
    }
  }, [error]);

  const navigate = useNavigate();

  const title = "Meet the team";
  const subtitle =
    "Tan importante como conocer el lugar, es conocer a la gente que hace el ambiente!";
  const layoutId = "meet-the-team";
  const titleLayoutId = layoutId + "-title";
  const progressBarLayoutId = layoutId + "-progress-bar";
  const color = "primary";

  const backHomeHandler = () => {
    navigate("/home", { state: { elevatedCard: layoutId } });
  };

  const completeSectionHandler = () => {
    try {
      const updatedUser = localUserProfileInteractor.setMeetTheTeamCompleted() as UserProfile;
      queryClient.setQueryData(["teamUsers"], (prev: Data | undefined) => ({
        ...prev!,
        uncompletedSections: mapUserProfileToUncompletedSections(updatedUser),
        completed: updatedUser.meetTheTeamCompleted,
      }));
    } catch (error) {
      throw error as CustomError;
    }
  };

  return {
    data: data ?? undefined,
    title,
    subtitle,
    layoutId,
    titleLayoutId,
    progressBarLayoutId,
    isLoading,
    isError,
    error,
    color,
    onBackHome: backHomeHandler,
    onFinish: completeSectionHandler,
  };
}
